import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Users, Mail, Phone, Globe, Shield, Eye, EyeOff, FileText,
  ClipboardCheck, Award, BookOpen, ChevronDown, ChevronUp, Search, Settings,
  ExternalLink, ToggleLeft, ToggleRight, Info, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCESS_OPTIONS = [
  { key: 'access_emargements', label: 'Émargements', icon: ClipboardCheck, description: 'Accès aux feuilles d\'émargement' },
  { key: 'access_documents', label: 'Documents', icon: FileText, description: 'Accès aux documents de formation' },
  { key: 'access_evaluations', label: 'Évaluations', icon: BookOpen, description: 'Accès aux résultats des évaluations' },
  { key: 'access_attestations', label: 'Attestations', icon: Award, description: 'Accès aux certificats et attestations' },
  { key: 'access_elearning', label: 'E-learning', icon: Globe, description: 'Accès au suivi e-learning' },
];

const EspaceEntreprise = ({ sessionId, clients = [] }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEntreprise, setExpandedEntreprise] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/enterprise-settings`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTogglePortal = async (ent) => {
    const updated = { ...ent, portal_enabled: !ent.portal_enabled };
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/enterprise-settings/${ent.entreprise_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(updated),
      });
      if (res.ok) {
        toast.success(updated.portal_enabled ? 'Portail activé' : 'Portail désactivé');
        fetchData();
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleToggleAccess = async (ent, key) => {
    const updated = { ...ent, [key]: !ent[key] };
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/enterprise-settings/${ent.entreprise_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(updated),
      });
      if (res.ok) {
        toast.success('Accès mis à jour');
        fetchData();
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const entreprises = data?.entreprises || [];
  const filtered = entreprises.filter(e =>
    e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.contact_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activePortals = entreprises.filter(e => e.portal_enabled).length;

  return (
    <div className="space-y-6" data-testid="espace-entreprise-content">
      <h2 className="text-xl font-bold text-slate-900 dark:text-white">Espace entreprise</h2>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="enterprise-summary">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-violet-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{entreprises.length}</p>
              <p className="text-xs text-slate-500">Entreprises</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Globe className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{activePortals}</p>
              <p className="text-xs text-slate-500">Portails actifs</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {entreprises.reduce((a, e) => a + (e.learner_count || 0), 0)}
              </p>
              <p className="text-xs text-slate-500">Apprenants rattachés</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info banner */}
      <div className="border border-violet-200 dark:border-violet-800 rounded-xl p-4 flex items-start gap-3 bg-violet-50/50 dark:bg-violet-900/10" data-testid="enterprise-info">
        <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0 mt-0.5">
          <Info className="h-3.5 w-3.5 text-white" />
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Donnez un accès dédié aux entreprises clientes pour suivre les formations de leurs collaborateurs, consulter les documents et les évaluations. Activez le portail pour chaque entreprise ci-dessous.
        </p>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input className="h-9 pl-9 text-xs" placeholder="Rechercher une entreprise..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-entreprises" />
        </div>
      </div>

      {/* Enterprise list */}
      <div className="space-y-3" data-testid="enterprise-list">
        {loading ? (
          <div className="py-12 text-center text-sm text-slate-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center" data-testid="no-entreprises">
            <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-1">Aucune entreprise</h3>
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              Ajoutez des entreprises dans le menu Données &gt; Entreprises pour les voir ici.
            </p>
          </div>
        ) : filtered.map((ent) => {
          const isExpanded = expandedEntreprise === ent.entreprise_id;
          return (
            <div key={ent.entreprise_id}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden transition-colors hover:border-violet-200"
              data-testid={`enterprise-${ent.entreprise_id}`}>
              {/* Header */}
              <div className="flex items-center gap-4 px-5 py-4 cursor-pointer"
                onClick={() => setExpandedEntreprise(isExpanded ? null : ent.entreprise_id)}>
                <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5 text-violet-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">{ent.name}</p>
                    <Badge variant="outline" className="text-xs">{ent.type || 'Entreprise'}</Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-1">
                    {ent.contact_name && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Users className="h-3 w-3" />{ent.contact_name}
                      </span>
                    )}
                    {ent.email && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Mail className="h-3 w-3" />{ent.email}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300">{ent.learner_count}</p>
                    <p className="text-xs text-slate-400">apprenants</p>
                  </div>
                  {/* Portal toggle */}
                  <button
                    className="flex items-center gap-2"
                    onClick={(e) => { e.stopPropagation(); handleTogglePortal(ent); }}
                    data-testid={`toggle-portal-${ent.entreprise_id}`}>
                    {ent.portal_enabled ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs gap-1">
                        <Globe className="h-3 w-3" />Portail actif
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-slate-400 gap-1">
                        <EyeOff className="h-3 w-3" />Portail inactif
                      </Badge>
                    )}
                  </button>
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="border-t border-slate-100 dark:border-slate-700/50 px-5 py-5 bg-slate-50/50 dark:bg-slate-800/50 space-y-5"
                  data-testid={`enterprise-details-${ent.entreprise_id}`}>
                  {/* Contact info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                      <p className="text-xs text-slate-400 mb-1">Contact</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{ent.contact_name || 'Non renseigné'}</p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                      <p className="text-xs text-slate-400 mb-1">Email</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{ent.email || 'Non renseigné'}</p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                      <p className="text-xs text-slate-400 mb-1">Téléphone</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{ent.phone || 'Non renseigné'}</p>
                    </div>
                  </div>

                  {/* Access configuration */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-violet-500" />Configuration des accès
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {ACCESS_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        const isActive = ent[opt.key];
                        return (
                          <button key={opt.key}
                            className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                              isActive
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                            }`}
                            onClick={() => handleToggleAccess(ent, opt.key)}
                            data-testid={`toggle-${opt.key}-${ent.entreprise_id}`}>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isActive ? 'bg-emerald-100 dark:bg-emerald-800' : 'bg-slate-100 dark:bg-slate-700'
                            }`}>
                              <Icon className={`h-4 w-4 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium ${isActive ? 'text-emerald-700' : 'text-slate-600'}`}>{opt.label}</p>
                              <p className="text-[10px] text-slate-400 truncate">{opt.description}</p>
                            </div>
                            {isActive ? (
                              <Eye className="h-4 w-4 text-emerald-500 shrink-0" />
                            ) : (
                              <EyeOff className="h-4 w-4 text-slate-300 shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Linked learners */}
                  {ent.linked_learners?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                        <Users className="h-4 w-4 text-violet-500" />Apprenants rattachés ({ent.linked_learners.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {ent.linked_learners.map((l, i) => (
                          <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full text-xs text-slate-600">
                            <Users className="h-3 w-3 text-violet-400" />{l.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    {ent.email && (
                      <Button variant="outline" className="text-xs gap-1.5" data-testid={`email-${ent.entreprise_id}`}>
                        <Send className="h-3.5 w-3.5" />Envoyer un e-mail
                      </Button>
                    )}
                    {ent.portal_enabled && (
                      <Button variant="outline" className="text-xs gap-1.5" data-testid={`portal-link-${ent.entreprise_id}`}>
                        <ExternalLink className="h-3.5 w-3.5" />Accéder au portail
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EspaceEntreprise;
